export const SITE_LOCALES = ['en', 'fr', 'ar'] as const
export type SiteLocale = (typeof SITE_LOCALES)[number]

export const DEFAULT_LOCALE: SiteLocale = 'en'

export const LOCALE_META: Record<
  SiteLocale,
  {
    htmlLang: string
    ogLocale: string
    dir: 'ltr' | 'rtl'
    hreflang: string
    label: string
    nativeLabel: string
  }
> = {
  en: {
    htmlLang: 'en',
    ogLocale: 'en_US',
    dir: 'ltr',
    hreflang: 'en',
    label: 'EN',
    nativeLabel: 'English',
  },
  fr: {
    htmlLang: 'fr',
    ogLocale: 'fr_FR',
    dir: 'ltr',
    hreflang: 'fr',
    label: 'FR',
    nativeLabel: 'Français',
  },
  ar: {
    htmlLang: 'ar',
    ogLocale: 'ar_MA',
    dir: 'rtl',
    hreflang: 'ar',
    label: 'AR',
    nativeLabel: 'العربية',
  },
}

// The 12 French-original resource articles (Morocco/medical-practice content)
// now also have English + Arabic translations. Unlike the 19 English-original
// articles below, French is the canonical/bare-URL language for these.
const FRENCH_ORIGINAL_RESOURCE_SLUGS = [
  'whatsapp-bloque-24-heures-api-officielle',
  'pourquoi-agent-ia-medical-a-besoin-sops',
  'premiers-patients-cabinet-medical-maroc',
  'contenu-medecin-smartphone-maroc',
  'visibilite-medecin-sans-video-maroc',
  'site-web-medical-seo-local-maroc',
  'pages-services-site-cabinet-medical',
  'choisir-agence-site-web-medical-maroc',
  'google-ads-meta-ads-cabinet-medical-maroc',
  'obtenir-premiers-patients-digital-cabinet-medical',
  'convertir-demandes-patients-rendez-vous',
  'structure-contenu-medecin-hook-valeur-cta',
] as const

const FRENCH_CANONICAL = [
  '/dentaire',
  '/case-studies/clinique-dentaire-casablanca-agent-ia-whatsapp',
  '/projects/demo-agent-ia-whatsapp-darija',
  '/projects/enrichissement-leads-ia',
  '/projects/landing-page-dentaire-automatisation-whatsapp',
  '/projects/site-cabinet-avocats',
  '/projects/plateforme-traduction-commandes',
  ...FRENCH_ORIGINAL_RESOURCE_SLUGS.map((slug) => `/resources/${slug}`),
] as const

// These commercial pages intentionally launch in French first because their
// keyword and SERP evidence is Morocco/fr specific. Until a translated page
// exists, the language switcher falls back to the matching services hub rather
// than inventing an English or Arabic URL that would soft-404.
const FRENCH_ONLY_SERVICE_PATHS = new Set([
  '/fr/services/assistant-whatsapp-centres-beaute',
  '/fr/services/assistant-whatsapp-spas-bien-etre',
  '/fr/services/chatbot-ia-maroc',
  '/fr/services/automatisation-ia-maroc',
  '/fr/services/n8n-maroc',
  '/fr/services/agence-marketing-digital-maroc',
  '/fr/services/marketing-centre-amincissement',
  '/fr/services/marketing-medecin-esthetique',
  '/fr/services/marketing-clinique-esthetique',
  '/morocco-doctors',
])

// The 19 English resource articles with real French + Arabic translations
// (src/data/resourceTranslations.fr.ts / .ar.ts), plus the 12 French-original
// articles above (translated to English + Arabic in
// src/data/resourceFirstPatients.en.ts / .ar.ts and resourceAdditions.en.ts / .ar.ts).
// Any future untranslated article stays out of this set.
const TRANSLATED_RESOURCE_SLUGS = new Set<string>([
  'how-ai-workflow-automation-works',
  'chatbot-vs-workflow-automation',
  'roi-of-automating-customer-support',
  'crm-automation-101',
  'signs-your-business-is-ready-for-ai-automation',
  'ai-automation-agency-vs-diy-tools',
  'ai-automation-for-small-business',
  'ai-automation-examples-small-business',
  'how-to-implement-ai-automation',
  'ai-automation-mistakes',
  'customer-service-automation-guide',
  'ai-chatbot-customer-service',
  'best-ai-workflow-automation-tools',
  'zapier-vs-make-vs-n8n',
  'ai-chatbot-moroccan-darija-dialects',
  'few-shot-prompting-vs-fine-tuning-vs-training',
  'vibe-coding-vs-n8n-workflows',
  'latest-ai-model-vs-best-model-benchmarking',
  'quality-first-ai-automation-cost-optimization',
  ...FRENCH_ORIGINAL_RESOURCE_SLUGS,
])

function splitHash(value: string): [string, string] {
  const index = value.indexOf('#')
  if (index === -1) return [value || '/', '']
  return [value.slice(0, index) || '/', value.slice(index)]
}

function normalizePath(path: string) {
  if (!path) return '/'
  const withSlash = path.startsWith('/') ? path : `/${path}`
  if (withSlash.length > 1 && withSlash.endsWith('/')) return withSlash.slice(0, -1)
  return withSlash
}

export function isMoroccoPath(pathname: string) {
  return pathname === '/ma' || pathname.startsWith('/ma/')
}

export function isFrenchCanonicalPath(pathname: string) {
  const logical = toLogicalPath(pathname)
  return FRENCH_CANONICAL.some((entry) => logical === entry || logical.startsWith(`${entry}/`))
}

function isGuideArticlePath(pathname: string) {
  const [path] = splitHash(normalizePath(pathname))
  return /^\/ma\/(?:en|fr|ar)\/guides\/[^/]+$/.test(path)
}

export function hasLanguageAlternates(pathname: string) {
  const [path] = splitHash(normalizePath(pathname))
  if (FRENCH_ONLY_SERVICE_PATHS.has(path)) return false
  const resourceMatch = path.match(/^(?:\/(?:en|fr|ar))?\/resources\/([^/]+)$/)
  if (resourceMatch && !TRANSLATED_RESOURCE_SLUGS.has(resourceMatch[1])) return false
  return true
}

export function getLocaleFromPathname(pathname: string): SiteLocale {
  const [path] = splitHash(normalizePath(pathname))
  if (path === '/fr' || path.startsWith('/fr/')) return 'fr'
  if (path === '/ar' || path.startsWith('/ar/')) return 'ar'
  if (path.startsWith('/ma/ar')) return 'ar'
  if (path.startsWith('/ma/fr')) return 'fr'
  if (path.startsWith('/ma/en')) return 'en'
  if (FRENCH_ONLY_SERVICE_PATHS.has(path)) return 'fr'
  if (path === '/en' || path.startsWith('/en/')) return 'en'
  if (FRENCH_CANONICAL.some((entry) => path === entry || path.startsWith(`${entry}/`))) return 'fr'
  return 'en'
}

export function toLogicalPath(pathname: string) {
  const [path] = splitHash(normalizePath(pathname))
  if (path.startsWith('/ma/')) return path.replace(/^\/ma\/(en|fr|ar)/, '/ma') || '/ma'
  if (/^\/(en|fr|ar)$/.test(path)) return '/'
  if (/^\/(en|fr|ar)\//.test(path)) return path.replace(/^\/(en|fr|ar)/, '') || '/'
  return path
}

export function href(logicalPath: string, locale: SiteLocale) {
  const [rawPath, hash] = splitHash(logicalPath)
  const path = normalizePath(rawPath)

  // These pages have no translated route. Keep their real URL for contextual links.
  if (FRENCH_ONLY_SERVICE_PATHS.has(path)) return `${path}${hash}`

  if (path === '/ma' || path.startsWith('/ma/')) {
    const rest = path.replace(/^\/ma(?:\/(?:en|fr|ar))?/, '') || ''
    return `/ma/${locale}${rest}${hash}`
  }

  if (FRENCH_CANONICAL.some((entry) => path === entry || path.startsWith(`${entry}/`))) {
    if (locale === 'fr') return `${path}${hash}`
    return `/${locale}${path}${hash}`
  }

  if (locale === DEFAULT_LOCALE) return `${path}${hash}`
  if (path === '/') return `/${locale}${hash}`
  return `/${locale}${path}${hash}`
}

export function canonicalPathFor(pathname: string) {
  const locale = getLocaleFromPathname(pathname)
  return href(toLogicalPath(pathname), locale)
}

export function switchLocale(currentPathname: string, target: SiteLocale) {
  const [path, hash] = splitHash(normalizePath(currentPathname))
  if (FRENCH_ONLY_SERVICE_PATHS.has(path)) {
    if (target === 'fr') return `${path}${hash}`
    return `${href('/services', target)}${hash}`
  }
  const resourceMatch = path.match(/^(?:\/(?:en|fr|ar))?\/resources\/([^/]+)$/)
  if (resourceMatch && !TRANSLATED_RESOURCE_SLUGS.has(resourceMatch[1])) {
    if (target === getLocaleFromPathname(path)) return `${path}${hash}`
    return `${href('/resources', target)}${hash}`
  }
  // Guide articles use a natural-language slug that is translated per
  // language (unlike the rest of /ma/*, where only city/specialty display
  // copy is translated and the slug itself stays fixed — French and Arabic
  // guide slugs even happen to share wording, English does not), so the
  // generic prefix swap below would guess a slug that may not exist in the
  // target language. Land on that language's guides hub instead, same
  // fallback used above for untranslated resource articles.
  if (isGuideArticlePath(path)) {
    if (target === getLocaleFromPathname(path)) return `${path}${hash}`
    return `/ma/${target}/guides${hash}`
  }
  return `${href(toLogicalPath(path), target)}${hash}`
}

export function hreflangFor(pathname: string, locale: SiteLocale) {
  const [path] = splitHash(normalizePath(pathname))
  if (isMoroccoPath(path) || isFrenchCanonicalPath(path)) {
    if (locale === 'fr') return 'fr-MA'
    if (locale === 'ar') return 'ar-MA'
    return 'en'
  }
  return LOCALE_META[locale].hreflang
}

export function xDefaultPath(pathname: string) {
  const logical = toLogicalPath(pathname)
  if (logical === '/ma' || logical.startsWith('/ma/')) return href(logical, 'fr')
  if (FRENCH_CANONICAL.some((entry) => logical === entry || logical.startsWith(`${entry}/`))) return href(logical, 'fr')
  return href(logical, DEFAULT_LOCALE)
}

export function alternateUrls(pathname: string) {
  return SITE_LOCALES.map((locale) => ({
    locale,
    hreflang: hreflangFor(pathname, locale),
    path: switchLocale(pathname, locale),
  }))
}

export function htmlLangFor(pathname: string) {
  const locale = getLocaleFromPathname(pathname)
  const [path] = splitHash(normalizePath(pathname))
  if (isMoroccoPath(path) || isFrenchCanonicalPath(path)) {
    if (locale === 'fr') return 'fr-MA'
    if (locale === 'ar') return 'ar-MA'
    return 'en'
  }
  return LOCALE_META[locale].htmlLang
}
